import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/AdminPanelPage.css';
import { searchStudents } from '../services/StudentService';
import {
  giveAdmin,
  revokeAdmin,
  createAnnouncement,
  getAllAnnouncements,
  deleteAnnouncement,
  getAllUsersPaged,
  deleteStudent,
  searchPostsAndComments,
  deletePostAdmin,
  deleteCommentAdmin,
  sendEmail,
  getPasswordResetTickets,
  adminChangePassword,
  deletePasswordResetTicket
} from '../services/AdminService';


function AdminPanelPage() {
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementContent, setAnnouncementContent] = useState('');
  const [announcementLink, setAnnouncementLink] = useState('');
  const [announcementPhotoUrl, setAnnouncementPhotoUrl] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [isCreatingAnnouncement, setIsCreatingAnnouncement] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [totalUsers, setTotalUsers] = useState(null);

  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');
  const [isSendingMail, setIsSendingMail] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');

  const [tickets, setTickets] = useState([]);


  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) navigate('/');
  }, [navigate, token]);

  const fetchAnnouncements = async () => {
    try {
      const res = await getAllAnnouncements(token);
      setAnnouncements(res.data || []);
    } catch (err) {
      console.error(err.message);
      alert('Duyurular yüklenirken hata oluştu: ' + err.message);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = userSearch
        ? await searchStudents(userSearch, page, token)
        : await getAllUsersPaged(page, token);

      setUsers(res.data || []);

      // total count'u da state'e al
      if (res.responseMessage) {
        setTotalUsers(res.responseMessage); // backend ResponseMessage içine toplam kullanıcı sayısını yazmalı
      }
    } catch (err) {
      console.error(err.message);
      alert('Kullanıcılar yüklenirken hata oluştu: ' + err.message);
    }
  };

  const fetchTickets = async () => {
    try {
      const res = await getPasswordResetTickets();
      setTickets(res.data || []);
    } catch (err) {
      console.error(err.message);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    fetchUsers();
    fetchTickets();
  }, [page, userSearch]); // search değişirse de yeniden fetch et


  const handleGiveAdmin = async () => {
    if (!username.trim()) return alert('Kullanıcı adı giriniz');
    try {
      await giveAdmin(username, token);
      alert(`${username} artık admin.`);
      setUsername('');
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRevokeAdmin = async () => {
    if (!username.trim()) return alert('Kullanıcı adı giriniz');
    try {
      await revokeAdmin(username, token);
      alert(`${username} artık admin değil.`);
      setUsername('');
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const sasToken = process.env.REACT_APP_AZURE_BLOB_SAS_TOKEN;

  const handleImageUpload = async (file) => {
    if (!file) return;
    const blobName = `${Date.now()}-${file.name}`;
    const uploadUrl = `https://iklmezunagi.blob.core.windows.net/iklphoto/${blobName}?${sasToken}`;
    try {
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload başarısız! Status: ${res.status}`);
      return `https://iklmezunagi.blob.core.windows.net/iklphoto/${blobName}`;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementTitle.trim() || !announcementContent.trim()) return alert('Başlık ve içerik zorunludur!');
    if (isCreatingAnnouncement) return;

    setIsCreatingAnnouncement(true);
    try {
      let fullContent = announcementContent;
      if (announcementLink.trim()) fullContent += `\nlink:${announcementLink.trim()}`;
      await createAnnouncement(announcementTitle, fullContent, announcementPhotoUrl, token);
      alert('Duyuru başarıyla oluşturuldu!');
      setAnnouncementTitle('');
      setAnnouncementContent('');
      setAnnouncementLink('');
      setAnnouncementPhotoUrl('');
      setUploadError('');
      fetchAnnouncements();
    } catch (err) {
      alert('Duyuru oluşturulamadı: ' + err.message);
    } finally {
      setIsCreatingAnnouncement(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Bu duyuruyu silmek istediğinize emin misiniz?')) return;
    try {
      await deleteAnnouncement(id, token);
      fetchAnnouncements();
      alert('Duyuru silindi');
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return;
    try {
      await deleteStudent(id, token);
      alert('Kullanıcı silindi.');
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSearchPostsComments = async (e) => {
    e.preventDefault();
    if (!searchKeyword.trim()) return;

    try {
      const results = await searchPostsAndComments(searchKeyword, token);
      const posts = (results.posts || []).map(p => ({ ...p, type: 'post' }));
      const comments = (results.comments || []).map(c => ({ ...c, type: 'comment' }));
      setSearchResults([...posts, ...comments]);
    } catch (err) {
      alert('Arama sırasında hata oluştu: ' + err.message);
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Bu postu silmek istediğinize emin misiniz?')) return;
    try {
      await deletePostAdmin(id, token);
      setSearchResults(prev => prev.filter(item => item.id !== id));
      alert('Post silindi.');
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteComment = async (id) => {
    if (!window.confirm('Bu yorumu silmek istediğinize emin misiniz?')) return;
    try {
      await deleteCommentAdmin(id, token);
      setSearchResults(prev => prev.filter(item => item.id !== id));
      alert('Yorum silindi.');
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSearchUsers = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };


  const handleSendEmail = async () => {
    if (!mailSubject.trim() || !mailBody.trim()) {
      alert('Konu ve içerik zorunludur!');
      return;
    }

    if (isSendingMail) return;
    setIsSendingMail(true);

    try {
      // 🧩 3 parametre gönderiyoruz: subject, body, recipients
      await sendEmail(mailSubject, mailBody, emailRecipients);
      alert('E-postalar başarıyla gönderildi!');
      setMailSubject('');
      setMailBody('');
      setEmailRecipients('');
    } catch (err) {
      alert('E-posta gönderimi başarısız: ' + err.message);
    } finally {
      setIsSendingMail(false);
    }
  };

  const handleProcessTicket = async (email) => {
    const newPassword = prompt(`"${email}" için yeni şifreyi giriniz:`);
    if (!newPassword) return;

    try {
      await adminChangePassword(email, newPassword);
      alert('Şifre başarıyla değiştirildi ve kullanıcıya mail gönderildi.');
      fetchTickets(); // Listeyi güncelle
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteTicket = async (id) => {
    if (!window.confirm("Bu isteği silmek istediğinize emin misiniz?")) return;
    try {
      await deletePasswordResetTicket(id);
      setTickets(prev => prev.filter(t => t.id !== id));
      alert("Ticket silindi.");
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <>
      <Navbar />
      <div className="admin-panel-container">
        <h1>Admin Paneli</h1>

        {/* Admin Yetki */}
        <div className="admin-section">
          <h3>Kullanıcıya Admin Yetkisi Ver/Kaldır</h3>
          <input type="text" placeholder="Kullanıcı adı" value={username} onChange={(e) => setUsername(e.target.value)} />
          <div className="admin-action-buttons">
            <button onClick={handleGiveAdmin}>Admin Yap</button>
            <button onClick={handleRevokeAdmin}>Adminliği Kaldır</button>
          </div>
        </div>

        {/* Şifre Sıfırlama İstekleri */}
        <div className="admin-section">
          <h3>Şifre Sıfırlama İstekleri (Ticketlar)</h3>
          {tickets.length === 0 ? <p>Bekleyen istek yok.</p> : (
            <div className="users-table-container">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>E-Posta</th>
                    <th>Tarih</th>
                    <th>Durum</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(ticket => (
                    <tr key={ticket.id}>
                      <td>{ticket.email}</td>
                      <td>{new Date(ticket.createdAt).toLocaleString()}</td>
                      <td>{ticket.isProcessed ? 'İşlendi' : 'Bekliyor'}</td>
                      <td>
                        <button onClick={() => handleProcessTicket(ticket.email)}
                          style={{ backgroundColor: '#ffc107', color: 'black', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', marginRight: '5px' }}>
                          Şifre Değiştir
                        </button>
                        <button onClick={() => handleDeleteTicket(ticket.id)}
                          style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                          Sil
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Duyuru Oluştur */}
        <div className="admin-section">
          <h3>Yeni Duyuru Oluştur</h3>
          <input type="text" placeholder="Başlık*" value={announcementTitle} onChange={(e) => setAnnouncementTitle(e.target.value)} required />
          <textarea placeholder="İçerik*" value={announcementContent} onChange={(e) => setAnnouncementContent(e.target.value)} rows={4} required />
          <input type="text" placeholder="İsteğe bağlı link (https://...)" value={announcementLink} onChange={(e) => setAnnouncementLink(e.target.value)} />

          <div className="photo-upload">
            <label htmlFor="photo-upload-input">{announcementPhotoUrl ? 'Fotoğraf Değiştir' : 'Fotoğraf Yükle (opsiyonel)'}</label>
            <input id="photo-upload-input" type="file" accept="image/*" style={{ display: 'none' }}
              onChange={async (e) => {
                try {
                  const file = e.target.files[0];
                  if (!file) return;
                  const uploadedUrl = await handleImageUpload(file);
                  setAnnouncementPhotoUrl(uploadedUrl);
                  setUploadError('');
                } catch (err) {
                  setUploadError('Fotoğraf yükleme başarısız: ' + err.message);
                }
              }}
            />
            {announcementPhotoUrl && (
              <div className="photo-preview">
                <img src={announcementPhotoUrl} alt="Duyuru Fotoğrafı" />
                <button className="remove-photo" onClick={() => setAnnouncementPhotoUrl('')}>×</button>
              </div>
            )}
            {uploadError && <p className="upload-error">{uploadError}</p>}
          </div>
          <button onClick={handleCreateAnnouncement} disabled={isCreatingAnnouncement}>
            {isCreatingAnnouncement ? 'Yayınlanıyor...' : 'Duyuru Yayınla'}
          </button>
        </div>

        {/* Duyurular */}
        <div className="admin-section">
          <h3>Duyurular</h3>
          {announcements.length === 0 ? <p>Henüz duyuru yok.</p> :
            <div className="announcements-container">
              {announcements.map((a) => (
                <div key={a.id} className="announcement-item">
                  <div className="announcement-header">
                    <h4>{a.title}</h4>
                    <span className="delete-icon" onClick={() => handleDeleteAnnouncement(a.id)} title="Sil">❌</span>
                  </div>
                  <div className="announcement-content">
                    <p>{a.content}</p>
                    {a.photoUrl && <img src={a.photoUrl} alt="Duyuru Fotoğrafı" className="announcement-image" />}
                  </div>
                  <div className="announcement-footer">
                    <span>{new Date(a.createdAt).toLocaleDateString()}</span>
                    <span>{a.createdBy}</span>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>

        {/* Post & Comment Arama */}
        <div className="admin-section">
          <h3>Post ve Comment Arama</h3>
          <form onSubmit={handleSearchPostsComments} className="search-form">
            <input
              type="text"
              placeholder="Anahtar kelime giriniz..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
            />
            <button type="submit">Ara</button>
          </form>

          {searchResults.length === 0 ? (
            <p>Sonuç bulunamadı.</p>
          ) : (
            <div className="search-results-container">
              {searchResults.map((item) => {
                const isPost = item.type === 'post';
                return (
                  <div
                    key={isPost ? item.id : item.commentId}
                    className={`search-result-item ${isPost ? 'post' : 'comment'}`}
                  >
                    <p className="content">{isPost ? item.content : item.commentContent}</p>
                    <button
                      className="delete-btn"
                      onClick={() =>
                        isPost ? handleDeletePost(item.id) : handleDeleteComment(item.commentId)
                      }
                    >
                      Sil
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>


        {/* E-Posta Gönder */}
        <div className="admin-section email-section">
          <h3>E-Posta Gönder</h3>
          <input
            type="text"
            placeholder="Kime (virgülle ayrılmış e-postalar, boşsa tüm kullanıcılara)"
            value={emailRecipients}
            onChange={(e) => setEmailRecipients(e.target.value)}
          />
          <input
            type="text"
            placeholder="Konu*"
            value={mailSubject}
            onChange={(e) => setMailSubject(e.target.value)}
            required
          />
          <textarea
            placeholder="İçerik*"
            value={mailBody}
            onChange={(e) => setMailBody(e.target.value)}
            rows={4}
            required
          />
          <button onClick={handleSendEmail} disabled={isSendingMail}>
            {isSendingMail ? 'Gönderiliyor...' : 'Gönder'}
          </button>
        </div>




        {/* Kullanıcı Yönetimi */}
        <div className="admin-section">
          <h3>Kullanıcıları Yönet</h3>

          {/* Toplam kullanıcı sayısı */}
          {totalUsers !== null && <p>Toplam Kullanıcı: {totalUsers}</p>}

          <form onSubmit={handleSearchUsers} className="user-search-form">
            <input
              type="text"
              placeholder="İsim / şehir / meslek"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <button type="submit">Ara</button>
          </form>

          {users.length === 0 ? (
            <p>Kullanıcı bulunamadı.</p>
          ) : (
            <div className="users-table-container">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Ad Soyad</th>
                    <th>Kullanıcı Adı</th>
                    <th>E-Posta</th>
                    <th>Telefon</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.firstName} {user.lastName}</td>
                      <td>{user.username}{user.isAdmin && <span className="admin-badge">Admin</span>}</td>
                      <td>{user.email || '-'}</td>
                      <td>{user.phoneNumber || '-'}</td>
                      <td>
                        <button onClick={() => handleDeleteUser(user.id)} className="delete-user-btn">
                          Sil
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Sayfalama */}
              <div className="pagination">
                <button
                  onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  Önceki
                </button>
                <span>Sayfa {page}</span>
                <button
                  onClick={() => setPage(prev => prev + 1)}
                  disabled={users.length < 9} // pageSize = 9 varsayım
                >
                  Sonraki
                </button>
              </div>
            </div>
          )}
        </div>



      </div>
      <Footer />
    </>

  );
}

export default AdminPanelPage;
